/**
 * Dial codes for the phone-number field's country dropdown.
 *
 * Deliberately CURATED, not exhaustive. Every entry here is a value nothing
 * can test — a wrong dial code silently corrupts a lead's callback number,
 * which is the worst thing this field can do. So the list covers the NRI
 * corridors and major markets the site actually sees, and stops there rather
 * than padding to ~240 entries of decreasing confidence.
 *
 * Nobody is locked out by the omissions: `PhoneField` accepts a number typed
 * with its own `+` prefix and ignores the dropdown in that case, so an
 * unlisted country still submits. Adding a country here is a one-line change.
 */
export interface DialCountry {
  /** ISO 3166-1 alpha-2 — the option value, since dial codes are not unique
   *  (+1 is shared by the US and Canada, +7 by Russia and Kazakhstan). */
  iso: string;
  name: string;
  /** E.164 country calling code, with its leading `+`. */
  dial: string;
}

/** Pinned to the top of the dropdown — where the site's buyers actually are. */
export const FREQUENT_ISO = [
  'IN',
  'AE',
  'US',
  'GB',
  'CA',
  'AU',
  'SG',
  'SA',
  'QA',
  'KW',
  'OM',
  'BH',
] as const;

export const DIAL_COUNTRIES: DialCountry[] = [
  { iso: 'AU', name: 'Australia', dial: '+61' },
  { iso: 'AT', name: 'Austria', dial: '+43' },
  { iso: 'BH', name: 'Bahrain', dial: '+973' },
  { iso: 'BD', name: 'Bangladesh', dial: '+880' },
  { iso: 'BE', name: 'Belgium', dial: '+32' },
  { iso: 'BR', name: 'Brazil', dial: '+55' },
  { iso: 'CA', name: 'Canada', dial: '+1' },
  { iso: 'CN', name: 'China', dial: '+86' },
  { iso: 'DK', name: 'Denmark', dial: '+45' },
  { iso: 'EG', name: 'Egypt', dial: '+20' },
  { iso: 'FI', name: 'Finland', dial: '+358' },
  { iso: 'FR', name: 'France', dial: '+33' },
  { iso: 'DE', name: 'Germany', dial: '+49' },
  { iso: 'GH', name: 'Ghana', dial: '+233' },
  { iso: 'GR', name: 'Greece', dial: '+30' },
  { iso: 'HK', name: 'Hong Kong', dial: '+852' },
  { iso: 'IN', name: 'India', dial: '+91' },
  { iso: 'ID', name: 'Indonesia', dial: '+62' },
  { iso: 'IE', name: 'Ireland', dial: '+353' },
  { iso: 'IL', name: 'Israel', dial: '+972' },
  { iso: 'IT', name: 'Italy', dial: '+39' },
  { iso: 'JP', name: 'Japan', dial: '+81' },
  { iso: 'JO', name: 'Jordan', dial: '+962' },
  { iso: 'KE', name: 'Kenya', dial: '+254' },
  { iso: 'KW', name: 'Kuwait', dial: '+965' },
  { iso: 'MY', name: 'Malaysia', dial: '+60' },
  { iso: 'MV', name: 'Maldives', dial: '+960' },
  { iso: 'MU', name: 'Mauritius', dial: '+230' },
  { iso: 'MX', name: 'Mexico', dial: '+52' },
  { iso: 'NP', name: 'Nepal', dial: '+977' },
  { iso: 'NL', name: 'Netherlands', dial: '+31' },
  { iso: 'NZ', name: 'New Zealand', dial: '+64' },
  { iso: 'NG', name: 'Nigeria', dial: '+234' },
  { iso: 'NO', name: 'Norway', dial: '+47' },
  { iso: 'OM', name: 'Oman', dial: '+968' },
  { iso: 'PK', name: 'Pakistan', dial: '+92' },
  { iso: 'PH', name: 'Philippines', dial: '+63' },
  { iso: 'PL', name: 'Poland', dial: '+48' },
  { iso: 'PT', name: 'Portugal', dial: '+351' },
  { iso: 'QA', name: 'Qatar', dial: '+974' },
  { iso: 'RU', name: 'Russia', dial: '+7' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '+966' },
  { iso: 'SG', name: 'Singapore', dial: '+65' },
  { iso: 'ZA', name: 'South Africa', dial: '+27' },
  { iso: 'KR', name: 'South Korea', dial: '+82' },
  { iso: 'ES', name: 'Spain', dial: '+34' },
  { iso: 'LK', name: 'Sri Lanka', dial: '+94' },
  { iso: 'SE', name: 'Sweden', dial: '+46' },
  { iso: 'CH', name: 'Switzerland', dial: '+41' },
  { iso: 'TW', name: 'Taiwan', dial: '+886' },
  { iso: 'TZ', name: 'Tanzania', dial: '+255' },
  { iso: 'TH', name: 'Thailand', dial: '+66' },
  { iso: 'TR', name: 'Turkey', dial: '+90' },
  { iso: 'UG', name: 'Uganda', dial: '+256' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '+971' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44' },
  { iso: 'US', name: 'United States', dial: '+1' },
  { iso: 'VN', name: 'Vietnam', dial: '+84' },
  { iso: 'ZM', name: 'Zambia', dial: '+260' },
  { iso: 'ZW', name: 'Zimbabwe', dial: '+263' },
];

/** India — the default selection, and the fallback if an ISO ever goes missing. */
export const DEFAULT_ISO = 'IN';

const BY_ISO = new Map(DIAL_COUNTRIES.map((c) => [c.iso, c]));

export function countryByIso(iso: string): DialCountry {
  return BY_ISO.get(iso) ?? BY_ISO.get(DEFAULT_ISO)!;
}

/** The two dropdown groups: pinned corridors first, then everything else A–Z. */
export const FREQUENT_COUNTRIES: DialCountry[] = FREQUENT_ISO.map(countryByIso);
export const OTHER_COUNTRIES: DialCountry[] = DIAL_COUNTRIES.filter(
  (c) => !FREQUENT_ISO.includes(c.iso as (typeof FREQUENT_ISO)[number]),
);
